<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">

    <div class="filter my-3">
      <div class="filter-head">
        <h4>{{ grade ? `مجموعات ${grade.name}` : 'مجموعات الطلاب' }}</h4>
        <div class="buttons">
          <button class="btn PrimaryButton" @click="openCreateDialog">
            <i class="pi pi-plus me-1"></i> إضافة مجموعة
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <table class="table table-striped">
          <thead>
            <tr>
              <th>#</th>
              <th>الاسم</th>
              <th>عدد الطلاب</th>
              <th>حالة الواتساب</th>
              <th>التحكم</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="5" class="text-center py-4">
                <i class="pi pi-spin pi-spinner" style="font-size:1.5rem;"></i>
              </td>
            </tr>
            <tr v-else-if="groups.length === 0">
              <td colspan="5" class="text-center py-4 text-muted">لا توجد مجموعات بعد</td>
            </tr>
            <tr v-for="(g, index) in groups" :key="g.id" v-else>
              <td>{{ index + 1 }}</td>
              <td>{{ g.name }}</td>
              <td>{{ g.students_count }}</td>
              <td>
                <span v-if="g.whatsapp_linked" class="status-badge status-badge--accepted">مرتبطة</span>
                <span v-else class="status-badge status-badge--pending">غير مرتبطة</span>
              </td>
              <td>
                <router-link class="btn btn-info text-white mx-1" :tooltip="'عرض الطلاب'" flow="up" :to="`/grades/${gradeSlug}/groups/${g.id}`">
                  <i class="pi pi-users"></i>
                </router-link>
                <button class="btn btn-primary text-white mx-1" :tooltip="'تعديل'" flow="up" @click="openEditDialog(g)">
                  <i class="pi pi-pencil"></i>
                </button>
                <router-link class="btn btn-success text-white mx-1" :tooltip="'واتساب'" flow="up" :to="`/grades/${gradeSlug}/groups/${g.id}/whatsapp`">
                  <i class="pi pi-whatsapp"></i>
                </router-link>
                <router-link class="btn btn-secondary text-white mx-1" :tooltip="'رسائل الحجز'" flow="up" :to="`/grades/${gradeSlug}/groups/${g.id}/messages`">
                  <i class="pi pi-envelope"></i>
                </router-link>
                <button class="btn btn-danger text-white mx-1" :tooltip="'حذف'" flow="up" @click="deleteGroup(g)">
                  <i class="pi pi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </div>

  <Dialog v-model:visible="dialogVisible" modal :header="editingGroup ? 'تعديل المجموعة' : 'إضافة مجموعة'" :style="{ width: '30rem' }">
    <div class="form-group d-flex flex-column">
      <label for="groupName">اسم المجموعة</label>
      <input id="groupName" type="text" class="form-control" v-model="groupName" placeholder="مثال: مجموعة أ" />
      <span class="text-danger mt-1" v-if="formError">{{ formError }}</span>
    </div>
    <div class="mt-3 text-end">
      <button class="btn PrimaryButton" :disabled="saving" @click="saveGroup">
        <i class="pi pi-spin pi-spinner-dotted" v-if="saving"></i>
        {{ saving ? 'جاري الحفظ...' : 'حفظ' }}
      </button>
    </div>
  </Dialog>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import Dialog from 'primevue/dialog';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: 'GradeGroupsIndex',
  components: { Dialog, Breadcrumb },

  data() {
    return {
      groups: [],
      loading: true,
      dialogVisible: false,
      editingGroup: null,
      groupName: '',
      formError: '',
      saving: false,
    };
  },

  computed: {
    gradeStore() { return useGradeStore(); },
    gradeSlug() { return this.$route.params.gradeSlug; },
    grade() {
      return this.gradeStore.activeGrades.find(g => g.slug === this.gradeSlug)
          || this.gradeStore.allGrades.find(g => g.slug === this.gradeSlug)
          || null;
    },
    breadcrumbs() {
      return [
        { name: 'الرئيسية', path: '/' },
        { name: this.grade?.name ?? 'المرحلة', path: `/grades/${this.gradeSlug}/students` },
        { name: 'مجموعات الطلاب', path: `/grades/${this.gradeSlug}/groups` },
      ];
    },
  },

  methods: {
    async fetchGroups() {
      if (!this.grade?.id) return;
      this.loading = true;
      try {
        const { data } = await axios.get(`/academic-years/${this.grade.id}/groups`);
        this.groups = data.data ?? [];
      } catch {
        this.groups = [];
      } finally {
        this.loading = false;
      }
    },

    openCreateDialog() {
      this.editingGroup = null;
      this.groupName = '';
      this.formError = '';
      this.dialogVisible = true;
    },

    openEditDialog(group) {
      this.editingGroup = group;
      this.groupName = group.name;
      this.formError = '';
      this.dialogVisible = true;
    },

    async saveGroup() {
      if (!this.groupName.trim()) {
        this.formError = 'برجاء إدخال اسم المجموعة';
        return;
      }
      this.saving = true;
      try {
        if (this.editingGroup) {
          const { data } = await axios.put(`/groups/${this.editingGroup.id}`, { name: this.groupName });
          const idx = this.groups.findIndex(g => g.id === this.editingGroup.id);
          if (idx !== -1) this.groups[idx] = data.data;
        } else {
          const { data } = await axios.post(`/academic-years/${this.grade.id}/groups`, {
            name: this.groupName,
          });
          this.groups.push(data.data);
        }
        this.dialogVisible = false;
      } catch (error) {
        this.formError = error.response?.data?.message || 'حدث خطأ أثناء الحفظ';
      } finally {
        this.saving = false;
      }
    },

    deleteGroup(group) {
      Swal.fire({
        title: `حذف مجموعة "${group.name}"؟`,
        text: 'سيتم إخراج كل الطلاب من هذه المجموعة',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'حذف',
        cancelButtonText: 'رجوع',
      }).then(r => {
        if (!r.isConfirmed) return;
        axios.delete(`/groups/${group.id}`).then(({ data }) => {
          if (data.status) {
            Swal.fire('', data.message, 'success');
            this.groups = this.groups.filter(g => g.id !== group.id);
          }
        });
      });
    },
  },

  async mounted() {
    if (!this.gradeStore.activeGrades.length) {
      await this.gradeStore.fetchActiveGrades();
    }
    if (!this.gradeStore.allGrades.length) {
      await this.gradeStore.fetchAllGrades();
    }
    this.fetchGroups();
  },
};
</script>

<style scoped>
</style>
